import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Share2, Flame } from "lucide-react";
import { publicApi } from "@/lib/api";
import { toast } from "sonner";

export default function Results() {
  const [data, setData] = useState(null);

  useEffect(() => {
    publicApi.results30().then(setData).catch(() => {});
  }, []);

  const s = data || { total: 0, wins: 0, losses: 0, voids: 0, strike_rate: 0, roi_points: 0, best_streak: 0, tips: [] };

  const share = () => {
    const text = `DailyRacer — last 30 days: ${s.strike_rate}% strike rate, ${
      s.roi_points >= 0 ? "+" : ""
    }${s.roi_points}pts ROI. One tip a day, £2 unlocks it. ${window.location.origin}`;
    const wa = `https://wa.me/?text=${encodeURIComponent(text)}`;
    if (navigator.share) {
      navigator
        .share({ title: "DailyRacer", text, url: window.location.origin })
        .catch(() => window.open(wa, "_blank"));
    } else {
      window.open(wa, "_blank");
    }
    toast.success("Sharing DailyRacer");
  };

  return (
    <div className="dr-shell pb-24">
      <header className="dr-glass sticky top-0 z-20 flex items-center gap-3 border-b px-6 py-4">
        <Link to="/" data-testid="results-back" className="text-zinc-400 hover:text-white">
          <ChevronLeft />
        </Link>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">DailyRacer</div>
          <div className="font-display text-lg leading-none text-white">How we're doing</div>
        </div>
        <button
          onClick={share}
          data-testid="whatsapp-share-btn"
          className="flex items-center gap-1 rounded-full border border-[#4ADE80]/40 bg-[#4ADE80]/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#4ADE80]"
        >
          <Share2 size={12} /> Share
        </button>
      </header>

      <section className="grid grid-cols-2 gap-3 px-6 pt-6">
        <StatCard label="Strike rate" value={`${s.strike_rate}%`} />
        <StatCard label="ROI (points)" value={`${s.roi_points > 0 ? "+" : ""}${s.roi_points}`} tint={s.roi_points >= 0 ? "green" : "red"} />
        <StatCard label="Winners" value={s.wins} />
        <StatCard label="Losers" value={s.losses} />
      </section>

      {s.best_streak >= 2 && (
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 px-6"
          data-testid="best-streak-badge"
        >
          <div className="relative overflow-hidden rounded-2xl border border-[#E1FF00]/40 bg-gradient-to-r from-[#E1FF00]/10 via-[#E1FF00]/5 to-[#E1FF00]/10 p-5">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#E1FF00]/15">
                <Flame size={20} className="text-[#E1FF00]" />
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#E1FF00]">
                  Best 30-day streak
                </div>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="font-display text-4xl leading-none text-white">
                    {s.best_streak}
                  </span>
                  <span className="text-sm text-zinc-400">winners in a row</span>
                </div>
              </div>
            </div>
          </div>
        </motion.section>
      )}

      <section className="mt-8 px-6" data-testid="results-list">
        <div className="mb-3 text-[10px] uppercase tracking-[0.24em] text-zinc-500">Last 30 days</div>
        <div className="divide-y divide-zinc-900 rounded-2xl border border-zinc-900 bg-zinc-950">
          {s.tips.length === 0 && (
            <div className="p-8 text-center text-sm text-zinc-500">No settled tips yet.</div>
          )}
          {s.tips.map((t, i) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, x: 8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.02 * i }}
              className="flex items-center justify-between px-5 py-4"
            >
              <div>
                <div className="font-mono-data text-xs text-zinc-500">
                  {t.race_date} · {t.race_time}
                </div>
                <div className="mt-1 font-display text-base text-white">{t.selection}</div>
                <div className="text-[11px] text-zinc-500">{t.meeting} · {t.odds}</div>
              </div>
              <ResultPill result={t.result} />
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}

function StatCard({ label, value, tint }) {
  const color = tint === "green" ? "text-[#4ADE80]" : tint === "red" ? "text-[#F87171]" : "text-white";
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5">
      <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">{label}</div>
      <div className={`mt-2 font-display text-3xl leading-none ${color}`}>{value}</div>
    </div>
  );
}

function ResultPill({ result }) {
  const map = {
    won: { label: "WON", cls: "bg-[#4ADE80]/10 text-[#4ADE80]" },
    lost: { label: "LOST", cls: "bg-[#F87171]/10 text-[#F87171]" },
    void: { label: "VOID", cls: "bg-zinc-800 text-zinc-400" },
  };
  const m = map[result] || { label: "—", cls: "bg-zinc-800 text-zinc-400" };
  return (
    <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] ${m.cls}`}>
      {m.label}
    </span>
  );
}
